import React, { useState, useEffect } from 'react';
import { Phone, Clock, Mail, Send, Loader, X, Star, CheckCircle, Zap, Heart, MessageCircle } from 'lucide-react';
import { createPortal } from 'react-dom';
import { saveContact } from '../services/firebase/contactService';
import LegalConsentCheckboxes, { validateConsents, formatConsentsForSubmission } from '../components/legal/LegalConsentCheckboxes';

const FloatingElements = () => {

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* נקודות מרקדות */}
      {[...Array(15)].map((_, i) => (
        <div
          key={`dot-${i}`}
          className="absolute w-1 h-1 bg-white/40 rounded-full animate-ping"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 3}s`,
            animationDuration: `${1 + Math.random() * 2}s`
          }}
        />
      ))}
      
      {/* עיגולים זוהרים */}
      {[...Array(6)].map((_, i) => (
        <div
          key={`glow-${i}`}
          className="absolute rounded-full animate-pulse opacity-20"
          style={{
            width: `${20 + Math.random() * 40}px`,
            height: `${20 + Math.random() * 40}px`,
            background: `conic-gradient(from ${Math.random() * 360}deg, #ff006e, #8338ec, #3a86ff, #06ffa5, #ffbe0b, #ff006e)`,
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 4}s`,
            animationDuration: `${3 + Math.random() * 2}s`,
            filter: 'blur(1px)'
          }}
        />
      ))}
    </div>
  );
};

const Modal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;
  
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center">
      <div 
        className="fixed inset-0 bg-black/70 backdrop-blur-sm" 
        onClick={onClose}
      />
      <div className="relative z-50 bg-gradient-to-t from-indigo-900 via-purple-800 to-pink-700 rounded-t-3xl shadow-2xl w-full max-w-sm mx-auto border-t-4 border-white/30 animate-slide-up">
        {children}
      </div>
    </div>,
    document.body
  );
};

const SuccessModal = ({ isOpen, onClose }) => (
  <Modal isOpen={isOpen} onClose={onClose}>
    <div className="p-6 text-center text-white">
      <div className="relative mb-4">
        <div className="w-16 h-16 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full mx-auto flex items-center justify-center animate-bounce">
          <CheckCircle className="w-8 h-8 text-white" />
        </div>
        <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-400 rounded-full animate-ping"></div>
      </div>
      <h3 className="text-xl font-bold mb-2">הפנייה נשלחה! 🎉</h3>
      <p className="text-white/80 text-sm">נחזור אליכם בהקדם האפשרי</p>
    </div>
  </Modal>
);

const ContactPage = () => {
  const [loading, setLoading] = useState(false);
  const [showHours, setShowHours] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    category: 'פנייה כללית'
  });

  // 🆕 מצב ההסכמות המשפטיות
  const [consents, setConsents] = useState({
    terms: false,
    dataTransfer: false,
    marketing: false
  });

      useEffect(() => {
    // גלילה חלקה לראש העמוד
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  const categories = [
    { value: 'פנייה כללית', label: '💬 פנייה כללית' },
    { value: 'תמיכה טכנית', label: '🔧 תמיכה טכנית' },
    { value: 'ייעוץ פיננסי', label: '💰 ייעוץ פיננסי' },
    { value: 'שיתוף פעולה', label: '🤝 שיתוף פעולה' },
    { value: 'משובים והצעות', label: '💡 משובים והצעות' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // 🆕 טיפול בהסכמות משפטיות
  const handleConsentChange = (newConsents) => {
    setConsents(newConsents);
  };

  const handleSubmit = async () => {
    // בדיקת תקינות בסיסית
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      alert('אנא מלאו את כל השדות החובה');
      return;
    }

    // 🆕 בדיקת הסכמות משפטיות חובה
    if (!validateConsents(consents, ['terms'])) {
      alert('יש לאשר את תנאי השימוש כדי להמשיך');
      return;
    }

    setLoading(true);
    
    try {
      console.log('📧 Sending contact form:', formData);
      
      // הכנת הנתונים לשליחה
      const contactData = {
        ...formData,
        // 🆕 הוספת ההסכמות המשפטיות
        ...formatConsentsForSubmission(consents)
      };
      
      const contactId = await saveContact(contactData);
      
      console.log('✅ Contact saved successfully with ID:', contactId);
      
      // הצגת הודעת הצלחה
      setShowSuccess(true);
      
      // איפוס הטופס לאחר הצלחה
      setTimeout(() => {
        setShowSuccess(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
          category: 'פנייה כללית'
        });
        setConsents({
          terms: false,
          dataTransfer: false,
          marketing: false
        });
      }, 2000);
      
    } catch (error) {
      console.error('❌ Error saving contact:', error);
      alert(`אירעה שגיאה: ${error.message}\nאנא נסו שוב מאוחר יותר.`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slide-up {
        from { transform: translateY(100%); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }
      
      @keyframes rainbow {
        0% { filter: hue-rotate(0deg); }
        100% { filter: hue-rotate(360deg); }
      }
      
      @keyframes shake {
        0%, 100% { transform: translateX(0); }
        25% { transform: translateX(-2px); }
        75% { transform: translateX(2px); }
      }
      
      @keyframes float {
        0%, 100% { transform: translateY(0px) rotate(0deg); }
        50% { transform: translateY(-5px) rotate(2deg); }
      }
      
      .animate-slide-up { animation: slide-up 0.3s ease-out; }
      .animate-rainbow { animation: rainbow 3s linear infinite; }
      .animate-shake { animation: shake 0.5s ease-in-out; }
      .animate-float { animation: float 2s ease-in-out infinite; }
      
      .glass-morphism {
        background: rgba(255, 255, 255, 0.08);
        backdrop-filter: blur(16px);
        border: 1px solid rgba(255, 255, 255, 0.2);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
      }
      
      .neon-glow {
        box-shadow: 0 0 20px rgba(139, 92, 246, 0.5), 0 0 40px rgba(139, 92, 246, 0.2);
      }
      
      .input-glow:focus {
        box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.3), 0 0 20px rgba(139, 92, 246, 0.2);
        border-color: #8b5cf6;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.contains(style) && document.head.removeChild(style);
  }, []);

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 overflow-hidden">
      {/* רקע מטורף */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_50%,rgba(120,119,198,0.3),transparent)] animate-pulse"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(255,119,198,0.3),transparent)] animate-pulse" style={{animationDelay: '1s'}}></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_40%_80%,rgba(119,255,198,0.3),transparent)] animate-pulse" style={{animationDelay: '2s'}}></div>
      </div>
      
      <FloatingElements />

      <div className="relative z-10 px-4 py-8">
        {/* הדר קומפקטי */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 sm:gap-3 mb-6">
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">צור קשר</h1>
              <p className="text-sm sm:text-base lg:text-lg text-white/70">גמלנט AI</p>
            </div>
          </div>
        </div>

        {/* כפתורי קשר מהיר */}
        <div className="mb-8">
          <div className="flex gap-2 sm:gap-3 justify-center">
            {[
              { icon: Phone, href: "tel:052-5513098", color: "from-green-400 to-emerald-500", label: "📞", title: "התקשר" },
              { icon: MessageCircle, href: "https://wa.me/972525513098", color: "from-green-500 to-green-600", label: "💬", title: "וואטסאפ" },
              { icon: Mail, href: "mailto:office@gemelnet.ai", color: "from-blue-400 to-blue-500", label: "✉️", title: "מייל" },
              { icon: Clock, onClick: () => setShowHours(true), color: "from-orange-400 to-orange-500", label: "🕐", title: "שעות" }
            ].map(({ icon: Icon, href, onClick, color, label, title }, i) => (
              <div key={i} className="animate-float" style={{animationDelay: `${i * 0.1}s`}}>
                {href ? (
                  <a href={href} target={href.includes('wa.me') ? '_blank' : undefined} 
                     className={`block w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 bg-gradient-to-r ${color} rounded-xl flex flex-col items-center justify-center glass-morphism hover:scale-110 transform transition-all duration-300 neon-glow`}
                     title={title}>
                    <span className="text-lg sm:text-xl lg:text-2xl">{label}</span>
                    <span className="text-xs sm:text-sm lg:text-base text-white/90 font-medium">{title}</span>
                  </a>
                ) : (
                  <button onClick={onClick}
                          className={`w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 bg-gradient-to-r ${color} rounded-xl flex flex-col items-center justify-center glass-morphism hover:scale-110 transform transition-all duration-300 neon-glow`}
                          title={title}>
                    <span className="text-lg sm:text-xl lg:text-2xl">{label}</span>
                    <span className="text-xs sm:text-sm lg:text-base text-white/90 font-medium">{title}</span>
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* טופס מעודכן */}
        <div className="max-w-2xl mx-auto">
          <div className="glass-morphism rounded-2xl p-6 sm:p-8 lg:p-10 shadow-2xl border border-white/30 neon-glow">
            <div className="text-center mb-6">
              <div className="w-14 h-14 sm:w-16 sm:h-16 lg:w-18 lg:h-18 bg-gradient-to-r from-pink-500 via-purple-600 to-indigo-600 rounded-xl flex items-center justify-center mx-auto mb-4 animate-pulse">
                <Send className="w-7 h-7 sm:w-8 sm:h-8 lg:w-9 lg:h-9 text-white" />
              </div>
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-2">השאירו פרטים 🚀</h2>
              <p className="text-white/70 text-sm sm:text-base">נחזור אליכם תוך 24 שעות</p>
            </div>
            
            <div className="space-y-4 sm:space-y-5">
              {/* שם ואימייל בשורה אחת */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full p-3 sm:p-4 bg-white/10 backdrop-blur-sm border border-white/30 rounded-lg input-glow outline-none transition-all duration-300 text-white placeholder-white/60 text-sm sm:text-base"
                  placeholder="שם מלא *"
                  dir="rtl"
                />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full p-3 sm:p-4 bg-white/10 backdrop-blur-sm border border-white/30 rounded-lg input-glow outline-none transition-all duration-300 text-white placeholder-white/60 text-sm sm:text-base"
                  placeholder="אימייל *"
                  dir="rtl"
                />
              </div>

              {/* טלפון וקטגוריה */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full p-3 sm:p-4 bg-white/10 backdrop-blur-sm border border-white/30 rounded-lg input-glow outline-none transition-all duration-300 text-white placeholder-white/60 text-sm sm:text-base"
                  placeholder="טלפון"
                  dir="rtl"
                />
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className="w-full p-3 sm:p-4 bg-white/10 backdrop-blur-sm border border-white/30 rounded-lg input-glow outline-none transition-all duration-300 text-white text-sm sm:text-base appearance-none"
                  dir="rtl"
                >
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value} className="bg-gray-800 text-white">
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* נושא */}
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                className="w-full p-3 sm:p-4 bg-white/10 backdrop-blur-sm border border-white/30 rounded-lg input-glow outline-none transition-all duration-300 text-white placeholder-white/60 text-sm sm:text-base"
                placeholder="נושא ההודעה *"
                dir="rtl"
              />

              {/* הודעה */}
              <textarea
                name="message"
                rows={4}
                value={formData.message}
                onChange={handleInputChange}
                className="w-full p-3 sm:p-4 bg-white/10 backdrop-blur-sm border border-white/30 rounded-lg input-glow outline-none transition-all duration-300 text-white placeholder-white/60 text-sm sm:text-base resize-none"
                placeholder="כתבו את הודעתכם כאן... *"
                dir="rtl"
              />

              {/* 🆕 רכיב ההסכמות המשפטיות */}
              <div className="bg-white/5 rounded-lg p-4 border border-white/20">
                <LegalConsentCheckboxes
                  variant="compact"
                  onConsentChange={handleConsentChange}
                  required={['terms']}
                showDataTransfer={true}
                  showMarketing={true}
                  className="text-white"
                />
              </div>

              {/* כפתור שליחה מעודכן */}
              <button
                onClick={handleSubmit}
                disabled={loading || !validateConsents(consents, ['terms'])}
                className={`
                  w-full p-3 sm:p-4 rounded-lg text-sm sm:text-base lg:text-lg font-bold
                  flex items-center justify-center gap-2
                  ${loading || !validateConsents(consents, ['terms'])
                    ? 'bg-gray-500/50 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-pink-500 via-purple-600 to-indigo-600 hover:from-pink-600 hover:via-purple-700 hover:to-indigo-700 animate-rainbow'
                  }
                  text-white transition-all duration-300
                  transform hover:scale-105 active:scale-95
                  shadow-xl hover:shadow-2xl neon-glow
                  border border-white/30
                `}
              >
                {loading ? (
                  <>
                    <Loader className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                    <span>שולח...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span>שלח הודעה</span>
                    <Zap className="w-4 h-4 sm:w-5 sm:h-5 animate-pulse" />
                  </>
                )}
              </button>
            </div>

            {/* סמלי אמינות */}
            <div className="mt-6 pt-4 border-t border-white/20">
              <div className="flex justify-center items-center gap-4 text-white/60 text-xs sm:text-sm">
                <div className="flex items-center gap-1">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span>מאובטח</span>
                </div>
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 text-yellow-400" />
                  <span>חינם</span>
                </div>
                <div className="flex items-center gap-1">
                  <Heart className="w-4 h-4 text-red-400 animate-pulse" />
                  <span>מענה מהיר</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* מודל שעות פעילות */}
      <Modal isOpen={showHours} onClose={() => setShowHours(false)}>
        <div className="p-4 text-white" dir="rtl">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-r from-orange-400 to-orange-500 rounded-lg flex items-center justify-center">
                <Clock className="w-4 h-4 text-white" />
              </div>
              <h3 className="text-lg font-bold">שעות פעילות</h3>
            </div>
            <button onClick={() => setShowHours(false)} className="p-1 hover:bg-white/10 rounded-full">
              <X className="w-4 h-4" />
            </button>
          </div>
          
          <div className="space-y-2 text-sm">
            <div className="flex justify-between py-2 px-3 bg-white/10 rounded-lg">
              <span>ראשון - חמישי</span>
              <span className="text-green-400 font-bold">09:00 - 18:00</span>
            </div>
            <div className="flex justify-between py-2 px-3 bg-white/10 rounded-lg">
              <span>שישי</span>
              <span className="text-orange-400 font-bold">09:00 - 14:00</span>
            </div>
            <div className="flex justify-between py-2 px-3 bg-white/10 rounded-lg">
              <span>שבת</span>
              <span className="text-red-400 font-bold">סגור</span>
            </div>
          </div>
          
          <div className="mt-4 p-3 bg-white/10 rounded-lg">
            <p className="text-xs text-center">
              <strong>📞 מענה מהיר:</strong> נחזור אליכם בהקדם האפשרי
            </p>
          </div>
        </div>
      </Modal>

      {/* מודל הצלחה */}
      <SuccessModal isOpen={showSuccess} onClose={() => setShowSuccess(false)} />
    </div>
  );
};

export default ContactPage;